import {
  type ChainFullId,
  type SignTransactionOptions,
  isSaplingAddress as isSaplingPkh,
  isValidImplicitPkh,
} from "@umami/chains";
import { useDynamicModalContext, useMultiForm } from "@umami/components";
import { useCurrentAccount, useGetNetworkSymbol, useGetSaplingAccount } from "@umami/state";
import { useCustomToast } from "@umami/utils";

import { PreviewTransactionPage } from "./PreviewTransactionPage";
import { type SendShieldedFormValues } from "./types";
import { usePrepareSapling } from "./usePrepareSapling";
import { usePrepareUnshield } from "./usePrepareUnshield";
import { getMnemonicFromOptions } from "../utils";

type UseSendShieldedFormProps = {
  chainId: ChainFullId;
  shieldedBalance: number;
  defaultToAddress?: string;
};

export type FormValues = {
  toAddress: string;
  amount: string;
  password: string;
  memo: string;
};

export const useSendShieldedForm = ({
  chainId,
  shieldedBalance,
  defaultToAddress,
}: UseSendShieldedFormProps) => {
  const toast = useCustomToast();
  const { openWith } = useDynamicModalContext();
  const currentAccount = useCurrentAccount();
  const saplingAccount = useGetSaplingAccount(currentAccount?.address.pkh ?? "");
  const symbol = useGetNetworkSymbol()(chainId);

  const form = useMultiForm<FormValues>({
    mode: "onBlur",
    defaultValues: {
      toAddress: defaultToAddress ?? "",
      amount: "",
      memo: "",
    },
  });

  const {
    formState: { errors },
    register,
    watch,
  } = form;

  const toAddress = watch("toAddress");
  const amount = watch("amount");
  const memo = watch("memo");

  const isSaplingAddress = isSaplingPkh(toAddress);
  const isUnshieldAddress = isValidImplicitPkh(toAddress);

  const previewButtonText = isSaplingAddress
    ? "Preview Shielded Transfer"
    : isUnshieldAddress
      ? "Preview Unshield Transaction"
      : "Preview";

  const { prepareSapling, isLoading: isSaplingLoading } = usePrepareSapling({
    toAddress: toAddress,
    amount: Number(amount) || 0,
    memo: memo,
  });

  const { prepareUnshield, isLoading: isUnshieldLoading } = usePrepareUnshield({
    toAddress: toAddress,
    amount: Number(amount) || 0,
  });

  const isLoading = isSaplingLoading || isUnshieldLoading;

  const handlePreview = async (formValues: FormValues, options: SignTransactionOptions) => {
    const mnemonic = getMnemonicFromOptions(options);

    const transaction = isSaplingAddress
      ? await prepareSapling(mnemonic)
      : await prepareUnshield(mnemonic);

    if (!transaction) {
      toast({
        description: "Error preparing transaction, please try again",
        status: "info",
      });

      return;
    }

    const sendShieldedFormValues: SendShieldedFormValues = {
      toAddress: formValues.toAddress,
      amount: formValues.amount,
      memo: formValues.memo || undefined,
    };

    await openWith(
      <PreviewTransactionPage
        chainId={chainId}
        formValues={sendShieldedFormValues}
        isSaplingTransaction={isSaplingAddress}
        transaction={transaction}
      />
    );
  };

  const validateAmount = (value: string) => {
    const numValue = parseFloat(value);

    if (isNaN(numValue) || numValue <= 0) {
      return "Amount must be greater than 0";
    }

    if (numValue > shieldedBalance) {
      return "Amount exceeds shielded balance";
    }

    return true;
  };

  const validateAddress = (value: string) => {
    if (!value) {
      return "Address is required";
    }

    const trimmedValue = value.trim();

    if (!isSaplingPkh(trimmedValue) && !isValidImplicitPkh(trimmedValue)) {
      return "Enter a valid Tezos address starting with 'zet' (sapling) or 'tz' (unshielded)";
    }

    return true;
  };

  return {
    form,
    errors,
    register,
    handleSubmit: (values: FormValues, options: SignTransactionOptions) =>
      handlePreview(values, options),
    isLoading,
    isSaplingAddress,
    previewButtonText,
    validateAmount,
    validateAddress,
    symbol,
    shieldedAddress: saplingAccount?.viewingKeyAddress,
  };
};
